import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import type { useAuth } from '@context/AuthContext';
import LoadingPage from '@pages/LoadingPage';
import { onBoardingRoutes } from './onBoardingRoutes';
import { unauthenticatedRoutes } from './unauthenticatedRoutes';
import { deliveryPersonRoutes } from './deliveryPersonRoutes';
import Layout from '@components/layout/Layout';
import { RouteErrorBoundary } from '@components/ErrorBoundary/AppErrorBoundary';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import useModules from '@utils/hooks/useModules';
import { lazy, Suspense } from 'react';
import { SelfServiceOrders } from '@pages/Orders/SelfServiceOrders';
import ProtectedModuleRoute from './ProtectedModuleRoute';
import { ModuleName } from '@utils/constants';
import { ENABLE_COMMERCE_V2, ENABLE_DELIVERY_V2, ENABLE_HORARIOS_V2, ENABLE_AJUSTES_V2 } from '@utils/featureFlags';
// Gate runtime de menu-v2: el build flag sigue siendo la capa 0 (availability), pero la
// elección V1/V2 la decide useFeatureVersion('menu-v2') en render (ver menuV2Gate.tsx).
import { Menu, Products, Extras, Sections } from './menuV2Gate';
const Stats = lazy(() => import('@pages/Stats'));
const CanceledDeliveryByProviderModal = lazy(() => import('@pages/Orders/components/CanceledDeliveryByProviderModal'));
const CancelDeliveryModal = lazy(() => import('@pages/Orders/components/CancelDeliveryModal'));
const RegisterOrderModal = lazy(() => import('@pages/Orders/RegisterOrderModal'));
const Orders = lazy(() => import('@pages/Orders/Orders'));
const GetModuleDescription = lazy(() => import('@pages/Subscriptionsv2/GetModuleDescription'));
const ConfirmEnableTrial = lazy(() => import('@pages/Subscriptionsv2/components/ConfirmEnableTrial'));
const AddUser = lazy(() => import('@pages/Users/AddUser'));
const Preferences = ENABLE_AJUSTES_V2
  ? lazy(() => import('@pages/Preferences/AjustesV2'))
  : lazy(() => import('@pages/Preferences/Preferences'));
const Users = lazy(() => import('@pages/Users/Users'));
const ResendNewPassword = lazy(() => import('@pages/Users/RecoveryUserPassword'));
const AddAdvertise = lazy(() => import('@pages/Adds/AddAdvertise'));
const AddCategory = lazy(() => import('@pages/Menu/Products/AddCategory'));
const AddExtra = lazy(() => import('@pages/Menu/Extras/AddExtra'));
// Alta/edición de producto en V4 (EditPageLayout). La versión V1 sigue en el árbol
// como `AddProduct.tsx` — ya sin consumidores — hasta validar en producción; ver la
// cabecera de AddProductV2.tsx.
const AddProduct = lazy(() => import('@pages/Menu/Products/AddProductV2'));
const Adds = lazy(() => import('@pages/Adds/Adds'));
const ScheduleAds = lazy(() => import('@pages/ScheduleAds/ScheduleAds'));
const AddSections = lazy(() => import('@pages/Menu/Sections/AddSection'));
const AddTurn = lazy(() => import('@pages/Schedule/AddTurn'));
const ChangePassword = lazy(() => import('@pages/Auth/ChangePassword'));
const CommerceData = ENABLE_COMMERCE_V2
  ? lazy(() => import('@pages/Commerce/CommerceDataV2'))
  : lazy(() => import('@pages/Commerce/CommerceData'));
const CommerceCategories = lazy(() => import('@pages/Commerce/modals/Categories'));
const DeliveryData = ENABLE_DELIVERY_V2
  ? lazy(() => import('@pages/Commerce/DeliveryDataV2'))
  : lazy(() => import('@pages/Commerce/DeliveryData'));
const Clients = lazy(() => import('@pages/Clients/Clients'));
const OrderDetails = lazy(() => import('@pages/Orders/OrderDetails'));
const OrdersHistory = lazy(() => import('@pages/Orders/OrdersHistory'));
const PageTutorials = lazy(() => import('@pages/Tutorials/PageTutorials'));
const Schedule = ENABLE_HORARIOS_V2
  ? lazy(() => import('@pages/Schedule/HorariosV2'))
  : lazy(() => import('@pages/Schedule/Schedule'));
const ShareMenu = lazy(() => import('@pages/ShareMenu/ShareMenu'));
const SortCategories = lazy(() => import('@pages/Menu/Products/SortCategories'));
const SortProducts = lazy(() => import('@pages/Menu/Products/SortProducts'));
const OnLineOrders = lazy(() => import('@pages/OnLineOrders/OnLineOrders'));
const Location = lazy(() => import('@pages/Commerce/modals/ChangeLocation'));
const DeliveryCostSimulator = lazy(() => import('@pages/Commerce/modals/DeliveryCostSimulator'));
const DeliveryDataConfig = lazy(() => import('@pages/Commerce/modals/DeliveryDataConfig'));
const WelcomeToFreemium = lazy(() => import('@pages/Subscriptionsv2/components/WelcomeToFreemium'));
const WelcomeToTrial = lazy(() => import('@pages/Subscriptionsv2/components/WelcomeToTrial'));
const MyAccount = lazy(() => import('@pages/Subscriptionsv2/MyAccount'));
const Coupons = lazy(() => import('@pages/Coupons/Coupons'));
const AddCoupon = lazy(() => import('@pages/Coupons/AddCoupon'));
const Integrations = lazy(() => import('@pages/Integrationsv3/Integrations'));
const VideoEmb = lazy(() => import('@pages/Tutorials/VideoEmb'));
const IntegrationModal = lazy(() => import('@pages/Integrationsv3/components/Modal'));
const IntegrationDetail = lazy(() => import('@pages/Integrationsv3/components/IntegrationDetail'));
const IntegrationConfig = lazy(() => import('@pages/Integrationsv3/components/IntegrationConfig'));
const TemplateEditorPage = lazy(() => import('@pages/Integrationsv3/components/providers/whatsapp/TemplateEditorPage'));
const TutorialDetail = lazy(() => import('@pages/Tutorials/TutorialDetail'));
const BulkPriceUpdate = lazy(() => import('@pages/Menu/Products/BulkPriceUpdate/BulkPriceUpdate'));
const BulkSectionAssign = lazy(() => import('@pages/Menu/Sections/BulkSectionAssign/BulkSectionAssign'));
const Tables = lazy(() => import('@pages/Tables/Tables'));
const Screens = lazy(() => import('@pages/Screens/Screens'));
const PlaylistEditor = lazy(() => import('@pages/Screens/PlaylistEditorPage'));
const SocialProfile = lazy(() => import('@pages/SocialProfile/SocialProfile'));
const Setup = lazy(() => import('@pages/Setup/SetupIndex'));
const MenuOnboardingPage = lazy(() => import('@pages/MenuOnboarding/MenuOnboardingPage'));
const WhatsappConversations = lazy(() => import('@pages/WhatsappConversations/WhatsappConversations'));

export const getRouter = (auth: ReturnType<typeof useAuth>) => {
  const { isFreemium, isTrial, isFounder } = useSubscription();
  const { getRootPath } = useModules();

  if (!auth.isReady) return createBrowserRouter(createRoutesFromElements(<Route path="*" element={<LoadingPage />} />));

  // PLI-2031: Router swap — si es delivery_person, cargar route tree aislado
  if (auth.isDeliveryPerson) return createBrowserRouter(deliveryPersonRoutes);

  if (!auth.isAuthenticated) return createBrowserRouter(unauthenticatedRoutes);

  // PLI-2031: Si la URL es /repartidor, usar rutas no autenticadas (tienen el landing del magic link)
  if (window.location.pathname.startsWith('/repartidor')) return createBrowserRouter(unauthenticatedRoutes);

  if (auth.onboardingDate === null) return createBrowserRouter(onBoardingRoutes);

  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />} errorElement={<RouteErrorBoundary />}>
        <Route index element={<Navigate to={getRootPath()} />} />
        <Route
          path="configuracion-inicial"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Setup />
            </Suspense>
          }
        />
        <Route
          path="cargar-menu"
          element={
            <Suspense fallback={<LoadingPage />}>
              <MenuOnboardingPage />
            </Suspense>
          }
        />
        <Route
          path="estadisticas"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Stats />
            </Suspense>
          }
        />

        <Route
          path="menu"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Menu />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="productos" />} />
          <Route
            path="productos"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Products />
              </Suspense>
            }
          >
            <Route
              path="ordenar-categorias"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <SortCategories />
                </Suspense>
              }
            />
            <Route
              path="ordenar-productos"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <SortProducts />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="extras"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Extras />
              </Suspense>
            }
          ></Route>
          <Route
            path="secciones"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Sections />
              </Suspense>
            }
          ></Route>
          <Route path="*" element={<Navigate to="/menu" />} />
        </Route>
        <Route
          path="menu/productos/nueva-categoria"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddCategory />
            </Suspense>
          }
        />
        <Route
          path="menu/productos/editar-categoria/:categoryId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddCategory />
            </Suspense>
          }
        />
        <Route
          path="menu/productos/nuevo-producto"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddProduct />
            </Suspense>
          }
        />
        <Route
          path="menu/productos/editar-producto/:productId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddProduct />
            </Suspense>
          }
        />
        {/* Rutas retiradas de los cargadores de menú legacy (MenuUploader/MenuUploaderV2):
            creaban un producto por presentación, descartaban las secciones detectadas y
            dejaban extras huérfanos. El flujo vigente es SmartMenuOnboarding (/cargar-menu),
            que sí crea secciones. Se redirige en vez de borrar la ruta para no romper
            marcadores/links guardados. */}
        <Route path="/menu/productos/procesar-menu" element={<Navigate to="/cargar-menu" replace />} />
        <Route path="/menu/productos/procesador-inteligente" element={<Navigate to="/cargar-menu" replace />} />
        <Route
          path="menu/productos/aumentar-precios-masivamente"
          element={
            <ProtectedModuleRoute module={ModuleName.MASS_UPDATE_PRODUCTS}>
              <Suspense fallback={<LoadingPage />}>
                <BulkPriceUpdate />
              </Suspense>
            </ProtectedModuleRoute>
          }
        />
        <Route
          path="menu/extras/nuevo-extra"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddExtra />
            </Suspense>
          }
        />
        <Route
          path="menu/extras/editar-extra/:extraId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddExtra />
            </Suspense>
          }
        />
        <Route
          path="menu/secciones/nueva-seccion"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddSections />
            </Suspense>
          }
        />
        <Route
          path="menu/secciones/editar-seccion/:sectionId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AddSections />
            </Suspense>
          }
        />
        <Route
          path="menu/secciones/asignar-masivo"
          element={
            <Suspense fallback={<LoadingPage />}>
              <BulkSectionAssign />
            </Suspense>
          }
        />
        <Route
          path="historial-pedidos"
          element={
            <Suspense fallback={<LoadingPage />}>
              <OrdersHistory />
            </Suspense>
          }
        >
          <Route
            path="detalle/:orderId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <OrderDetails />
              </Suspense>
            }
          >
            <Route
              path="cancelar-reparto"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <CancelDeliveryModal />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route
          path="control-pedidos"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Orders />
            </Suspense>
          }
        >
          <Route
            path="registrar-pedido"
            element={
              <Suspense fallback={<LoadingPage />}>
                <RegisterOrderModal />
              </Suspense>
            }
          />
          <Route
            path="reparto-cancelado/:orderId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <CanceledDeliveryByProviderModal />
              </Suspense>
            }
          />
          <Route
            path="detalle/:orderId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <OrderDetails />
              </Suspense>
            }
          >
            <Route
              path="cancelar-reparto"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <CancelDeliveryModal />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="self-service/:tableID/:tableNumber"
            element={
              <Suspense fallback={<LoadingPage />}>
                <SelfServiceOrders />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="pedidos-en-linea"
          element={
            <Suspense fallback={<LoadingPage />}>
              <OnLineOrders />
            </Suspense>
          }
        >
          <Route
            path="detalle/:orderId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <OrderDetails />
              </Suspense>
            }
          >
            <Route
              path="cancelar-reparto"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <CancelDeliveryModal />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route
          path="clientes"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Clients />
            </Suspense>
          }
        />
        {/* Bandeja de WhatsApp. Dos rutas al MISMO componente: la lista y el hilo
            son la misma pantalla y el id en la URL la hace enlazable. Gate a nivel de ruta con
            NOTIFICACIONES_CLIENTE_WHATSAPP (el mismo add-on que la seccion "Notificaciones a
            clientes" de Integraciones), para que la URL directa tampoco entre sin el modulo.
            Sin el add-on NO se rebota a la raiz: se manda a la pagina de descripcion, que es la
            misma superficie a la que lleva la entrada del menu (la entrada se ve siempre, ver
            NavBarDesktopV3). Vale para las dos rutas: un enlace directo a un hilo tambien tiene
            que caer en la oferta y no en el menu. */}
        <Route
          path="conversaciones"
          element={
            <ProtectedModuleRoute
              module={ModuleName.NOTIFICACIONES_CLIENTE_WHATSAPP}
              redirectTo="/suscripcion/conversaciones"
            >
              <Suspense fallback={<LoadingPage />}>
                <WhatsappConversations />
              </Suspense>
            </ProtectedModuleRoute>
          }
        />
        <Route
          path="conversaciones/:contactPhone"
          element={
            <ProtectedModuleRoute
              module={ModuleName.NOTIFICACIONES_CLIENTE_WHATSAPP}
              redirectTo="/suscripcion/conversaciones"
            >
              <Suspense fallback={<LoadingPage />}>
                <WhatsappConversations />
              </Suspense>
            </ProtectedModuleRoute>
          }
        />
        <Route
          path="anuncios"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Adds />
            </Suspense>
          }
        >
          <Route
            path="nuevo"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddAdvertise />
              </Suspense>
            }
          />
          <Route
            path="editar/:addId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddAdvertise />
              </Suspense>
            }
          />
          <Route
            path="agendar"
            element={
              <Suspense fallback={<LoadingPage />}>
                <ScheduleAds />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="compartir-menu"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ShareMenu />
            </Suspense>
          }
        />
        <Route
          path="cupones"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Coupons />
            </Suspense>
          }
        >
          <Route
            path="coupon/nuevo"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddCoupon />
              </Suspense>
            }
          />
          <Route
            path="coupon/edit/:couponId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddCoupon />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="asistencia"
          element={
            <Suspense fallback={<LoadingPage />}>
              <PageTutorials />
            </Suspense>
          }
        >
          <Route
            path="tutorial/:tutoId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <VideoEmb />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<LoadingPage />}>
                <TutorialDetail />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="datos-del-comercio"
          element={
            <Suspense fallback={<LoadingPage />}>
              <CommerceData />
            </Suspense>
          }
        >
          <Route
            path="cambiar-contrasena"
            element={
              <Suspense fallback={<LoadingPage />}>
                <ChangePassword />
              </Suspense>
            }
          />
          <Route
            path="categorias"
            element={
              <Suspense fallback={<LoadingPage />}>
                <CommerceCategories />
              </Suspense>
            }
          />
          <Route
            path="ubicacion"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Location />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="horarios"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Schedule />
            </Suspense>
          }
        >
          <Route
            path="nuevo"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddTurn />
              </Suspense>
            }
          />
          <Route
            path="editar/:turnIndex"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddTurn />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="tarifas"
          element={
            <Suspense fallback={<LoadingPage />}>
              <DeliveryData />
            </Suspense>
          }
        >
          <Route
            path="simulador"
            element={
              <Suspense fallback={<LoadingPage />}>
                <DeliveryCostSimulator />
              </Suspense>
            }
          />
          <Route
            path="delivery-config"
            element={
              <Suspense fallback={<LoadingPage />}>
                <DeliveryDataConfig />
              </Suspense>
            }
          />
        </Route>
        <Route path="configuracion-cobro-en-linea" element={<Navigate to="/integraciones" replace />} />
        <Route
          path="usuarios"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Users />
            </Suspense>
          }
        >
          <Route
            path="nuevo"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddUser />
              </Suspense>
            }
          />
          <Route
            path="editar/:userId"
            element={
              <Suspense fallback={<LoadingPage />}>
                <AddUser />
              </Suspense>
            }
          />
          <Route
            path="recuperar-contrasena/:userSelected"
            element={
              <Suspense fallback={<LoadingPage />}>
                <ResendNewPassword />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="ajustes"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Preferences />
            </Suspense>
          }
        />

        {/* Página de descripción de un módulo (upsell). Registrada para TODOS los planes, no solo
            freemium: hay add-ons que se compran aparte del plan (el de conversaciones de WhatsApp
            es el primero), así que un comercio de paga sin ese add-on también necesita llegar a
            la oferta. `GetModuleDescription` ya es agnóstica del plan — su CTA resuelve entre
            prueba gratuita y "ver planes". Para los 9 paths del catálogo que sí son módulos de
            plan el efecto es nulo: todos caen dentro de MODULES_PATH y PageWrapper los rebota
            igual que antes; '/suscripcion/conversaciones' no matchea ninguno. */}
        <Route
          path="suscripcion/:module"
          element={
            <Suspense fallback={<LoadingPage />}>
              <GetModuleDescription />
            </Suspense>
          }
        />
        {isFreemium && (
          <>
            <Route
              path="bienvenido-freemium"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <WelcomeToFreemium />
                </Suspense>
              }
            />
            <Route
              path="activar-trial"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <ConfirmEnableTrial />
                </Suspense>
              }
            />
          </>
        )}
        {isTrial && (
          <Route
            path="bienvenido-prueba-gratis"
            element={
              <Suspense fallback={<LoadingPage />}>
                <WelcomeToTrial />
              </Suspense>
            }
          />
        )}
        {!isFounder && (
          <Route
            path="mi-cuenta"
            element={
              <Suspense fallback={<LoadingPage />}>
                <MyAccount />
              </Suspense>
            }
          />
        )}
        <Route
          path="mesas"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Tables />
            </Suspense>
          }
        />
        {/* Pantallas (plick-screens): sin ProtectedModuleRoute a propósito — la página
            se auto-gatea por módulo SCREENS con preview + oferta (upsell visitable). */}
        <Route
          path="pantallas"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Screens />
            </Suspense>
          }
        />
        {/* Editor de playlist como PÁGINA (rutas duales nueva/editar, mismo componente).
            Fuera de <Route path="pantallas"> a propósito: es una pantalla completa, no una
            sub-vista de la lista. Se auto-gatea por módulo SCREENS igual que Screens.tsx —
            PageWrapper no la cubre (SCREENS no está en MODULES_PATH). */}
        <Route
          path="pantallas/playlists/nueva"
          element={
            <Suspense fallback={<LoadingPage />}>
              <PlaylistEditor />
            </Suspense>
          }
        />
        <Route
          path="pantallas/playlists/editar/:playlistId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <PlaylistEditor />
            </Suspense>
          }
        />
        <Route
          path="integraciones"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Integrations />
            </Suspense>
          }
        >
          <Route
            path=":providerType/:providerCode"
            element={
              <Suspense fallback={<LoadingPage />}>
                <IntegrationModal />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="integraciones/:providerType/:providerCode/detail"
          element={
            <Suspense fallback={<LoadingPage />}>
              <IntegrationDetail />
            </Suspense>
          }
        />
        <Route
          path="integraciones/:providerType/:providerCode/configuration"
          element={
            <Suspense fallback={<LoadingPage />}>
              <IntegrationConfig />
            </Suspense>
          }
        />
        <Route
          path="integraciones/:providerType/:providerCode/configuration/template/:status"
          element={
            <Suspense fallback={<LoadingPage />}>
              <TemplateEditorPage />
            </Suspense>
          }
        />

        <Route
          path="perfil-social"
          element={
            <Suspense fallback={<LoadingPage />}>
              <SocialProfile />
            </Suspense>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Route>,
    ),
  );
};
